import { NavItemType } from 'types';
import { Permission } from 'api/user-permissions';

/**
 * Check if user has full admin access (all permissions enabled for all modules)
 */
export const hasFullAdminAccess = (permissions: Permission[]): boolean => {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  // Check if all permissions have all actions enabled
  return permissions.every((p) => p.permissions.view && p.permissions.add && p.permissions.edit && p.permissions.delete);
};

/**
 * Filter menu items based on allowed modules
 */
export const filterMenuByPermissions = (items: NavItemType[], allowedModules: Set<string>): NavItemType[] => {
  return items
    .map((item) => ({
      ...item,
      children: item.children ? filterMenuByPermissions(item.children, allowedModules) : undefined
    }))
    .filter((item) => {
      const isContainer = item.type === 'group' || item.type === 'collapse';

      if (item.children && item.children.length > 0) {
        return true;
      }

      if (isContainer) {
        return false;
      }

      if (!item.id) {
        return true;
      }

      return allowedModules.has(item.id);
    });
};

/**
 * Create allowed modules set from permissions
 * Maps backend menuName to frontend menu IDs
 */
export const createAllowedModulesSet = (permissions: Permission[]): Set<string> => {
  const allowedSet = new Set<string>();

  permissions.forEach((p) => {
    if (p.permissions.view || p.permissions.add || p.permissions.edit || p.permissions.delete) {
      // Add the menuName as is
      allowedSet.add(p.menuName);
      
      // Add lowercase version for case-insensitive matching
      allowedSet.add(p.menuName.toLowerCase());
      
      // Convert kebab-case to camelCase for potential ID matching
      const camelCaseName = p.menuName
        .split('-')
        .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');
      allowedSet.add(camelCaseName);
    }
  });

  return allowedSet;
};

export const filterSingleMenuGroup = (menuGroup: NavItemType, allowedModules: Set<string>): NavItemType | null => {
  const filtered: NavItemType = {
    ...menuGroup,
    children: menuGroup.children ? filterMenuByPermissions(menuGroup.children, allowedModules) : undefined
  };

  if (filtered.type === 'collapse' && filtered.children && filtered.children.length > 0) {
    return filtered;
  }

  if (filtered.type === 'item' && filtered.id && allowedModules.has(filtered.id)) {
    return filtered;
  }

  if (filtered.type === 'group' && filtered.children && filtered.children.length > 0) {
    return filtered;
  }

  return null;
};
